import { Box, useTheme } from "@mui/material";
import type { Feature, LineString } from "geojson";
import type { GeoJSONSource } from "maplibre-gl";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "src/utils/maplibreWorker";
import { useEffect, useRef, useState } from "react";
import type {
  Coordinates,
  MillInfo,
  RailRouteResponse,
  TruckRoute,
} from "src/store/api/generatedApi";
import theme from "src/theme";

const MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

const ROUTES_SOURCE = "routes-source";
const ROUTES_LAYER = "routes-layer";
const ORIGIN_SOURCE = "origin-source";
const ORIGIN_LAYER = "origin-layer";
const MILLS_SOURCE = "mills-source";
const MILLS_LAYER = "mills-layer";
const TRUCK_ROUTES_SOURCE = "truck-routes-source";
const TRUCK_ROUTES_LAYER = "truck-routes-layer";
const ROUTE_OFFSET_SPACING = 2;

interface RailRouteMapProps {
  mills: MillInfo[];
  routes: Record<string, RailRouteResponse>;
  originCoordinates: Coordinates | null;
  truckRoutes: Record<string, TruckRoute>;
}

const routesToFeatures = (
  routes: Record<string, RailRouteResponse>,
  mills: MillInfo[]
): Feature<LineString>[] => {
  const colorByTenant = new Map(mills.map((m) => [m.tenant_name, m.color]));
  const tenantNames = [...new Set(Object.keys(routes))];
  const offsets = spreadOffsets(tenantNames.length);
  const offsetByTenant = new Map(tenantNames.map((t, i) => [t, offsets[i]]));

  return Object.entries(routes).map(
    ([tenantName, route]): Feature<LineString> => ({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: route.coordinates.map((coordinate) => [
          coordinate.longitude,
          coordinate.latitude,
        ]),
      },
      properties: {
        tenant_name: tenantName,
        color: colorByTenant.get(tenantName) ?? theme.palette.grey,
        offset: offsetByTenant.get(tenantName) ?? 0,
      },
    })
  );
};

const spreadOffsets = (count: number): number[] => {
  if (count <= 1) return [0];
  const total = (count - 1) * ROUTE_OFFSET_SPACING;
  return Array.from(
    { length: count },
    (_, i) => i * ROUTE_OFFSET_SPACING - total / 2
  );
};

export const RailRouteMap = ({
  mills,
  routes,
  originCoordinates,
  truckRoutes,
}: RailRouteMapProps) => {
  const theme = useTheme();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || mapRef.current) return;

    const map = new maplibregl.Map({
      container,
      style: MAP_STYLE,
      center: [-95, 39],
      zoom: 4,
      attributionControl: false,
    });

    map.on("load", () => {
      map.addSource(ROUTES_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: ROUTES_LAYER,
        type: "line",
        source: ROUTES_SOURCE,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 3,
          "line-opacity": 0.85,
          "line-offset": ["get", "offset"],
        },
      });

      map.addSource(TRUCK_ROUTES_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: TRUCK_ROUTES_LAYER,
        type: "line",
        source: TRUCK_ROUTES_SOURCE,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 3,
          "line-opacity": 0.7,
          "line-dasharray": [2, 2],
          "line-offset": ["get", "offset"],
        },
        layout: {
          "line-cap": "butt",
          "line-join": "round",
        },
      });

      map.addSource(ORIGIN_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: ORIGIN_LAYER,
        type: "circle",
        source: ORIGIN_SOURCE,
        paint: {
          "circle-color": theme.palette.text.primary,
          "circle-radius": 8,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#fff",
        },
      });

      map.addSource(MILLS_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: MILLS_LAYER,
        type: "circle",
        source: MILLS_SOURCE,
        paint: {
          "circle-color": ["get", "color"],
          "circle-radius": 12,
          "circle-stroke-width": 3,
          "circle-stroke-color": "#fff",
        },
      });

      setMapReady(true);
    });

    mapRef.current = map;

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => map.resize());
      ro.observe(container);
    }

    return () => {
      setMapReady(false);
      ro?.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, [theme.palette.text.primary]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;

    const setSourceData = (name: string, features: Feature[]) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- needed for tsc
      const source = map.getSource(name) as GeoJSONSource | undefined;
      void source?.setData({ type: "FeatureCollection", features });
    };

    setSourceData(ROUTES_SOURCE, routesToFeatures(routes, mills));

    const millColorMap = new Map(mills.map((m) => [m.tenant_name, m.color]));
    const truckTenantNames = Object.keys(truckRoutes);
    const truckOffsets = spreadOffsets(truckTenantNames.length);
    const truckOffsetByTenant = new Map(
      truckTenantNames.map((name, index) => [name, truckOffsets[index]])
    );
    const truckFeatures: Feature<LineString>[] = Object.entries(
      truckRoutes
    ).flatMap(([tenantName, route]) => {
      if (route.coordinates.length < 2) return [];
      return [
        {
          type: "Feature" as const,
          geometry: {
            type: "LineString" as const,
            coordinates: route.coordinates.map((c) => [
              c.longitude,
              c.latitude,
            ]),
          },
          properties: {
            color: millColorMap.get(tenantName) ?? "#888",
            tenant_name: tenantName,
            offset: truckOffsetByTenant.get(tenantName) ?? 0,
          },
        },
      ];
    });
    setSourceData(TRUCK_ROUTES_SOURCE, truckFeatures);

    setSourceData(
      ORIGIN_SOURCE,
      originCoordinates
        ? [
            {
              type: "Feature" as const,
              geometry: {
                type: "Point" as const,
                coordinates: [
                  originCoordinates.longitude,
                  originCoordinates.latitude,
                ],
              },
              properties: {},
            },
          ]
        : []
    );

    setSourceData(
      MILLS_SOURCE,
      mills.map((mill) => ({
        type: "Feature" as const,
        geometry: {
          type: "Point" as const,
          coordinates: [mill.location.longitude, mill.location.latitude],
        },
        properties: {
          color: mill.color,
          label: mill.display_name,
          mill_id: mill.tenant_name,
        },
      }))
    );

    const truckCoordinates = truckFeatures.flatMap((f) =>
      f.geometry.coordinates.map((c) => [c[0], c[1]] as [number, number])
    );
    const points: [number, number][] = [
      ...mills.map(
        (mill) =>
          [mill.location.longitude, mill.location.latitude] as [number, number]
      ),
      ...(originCoordinates
        ? [
            [originCoordinates.longitude, originCoordinates.latitude] as [
              number,
              number,
            ],
          ]
        : []),
      ...truckCoordinates,
    ];
    if (points.length > 0) {
      const bounds = points.reduce(
        (acc, point) => acc.extend(point),
        new maplibregl.LngLatBounds()
      );
      map.fitBounds(bounds, {
        padding: 100,
        duration: 800,
      });
    }
  }, [mills, routes, originCoordinates, truckRoutes, mapReady]);

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 500,
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: theme.shadows[4],
      }}
    />
  );
};
